import { useEffect, useState } from 'react';
import {
  listPLQEntries,
  createPLQEntry,
  deletePLQEntry,
  listPLQVersions,
  rollbackPLQEntry,
} from '../../api/projects';
import { toast } from '../../stores/uiStore';
import type { PLQEntry, VersionListItem } from '../../types/estimation';
import styles from './ProjectListPage.module.css';

interface Props {
  projectId?: string;
}

export function PLQEntryList({ projectId }: Props) {
  const [entries, setEntries] = useState<PLQEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [versionsFor, setVersionsFor] = useState<string | null>(null);
  const [versions, setVersions] = useState<VersionListItem[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    source: '',
    project_id: projectId ?? '',
  });

  useEffect(() => {
    loadEntries();
  }, [projectId]);

  async function loadEntries() {
    try {
      setLoading(true);
      const data = await listPLQEntries(projectId);
      setEntries(data);
    } catch (err) {
      console.error('Failed to load PLQ entries:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    try {
      await createPLQEntry({
        name: formData.name,
        source: formData.source,
        project_id: formData.project_id || undefined,
        change_summary: 'Initial creation',
      });
      toast.success('PLQ entry created.');
      setShowCreate(false);
      setFormData({ name: '', source: '', project_id: projectId ?? '' });
      await loadEntries();
    } catch (err) {
      console.error('Failed to create PLQ entry:', err);
      toast.error('Failed to create PLQ entry. Please try again.');
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this PLQ entry?')) return;
    try {
      await deletePLQEntry(id);
      toast.success('PLQ entry deleted.');
      await loadEntries();
    } catch (err) {
      console.error('Failed to delete PLQ entry:', err);
      toast.error('Failed to delete PLQ entry. Please try again.');
    }
  }

  async function showVersions(entryId: string) {
    if (versionsFor === entryId) {
      setVersionsFor(null);
      return;
    }
    try {
      const v = await listPLQVersions(entryId);
      setVersions(v);
      setVersionsFor(entryId);
    } catch (err) {
      console.error('Failed to load PLQ versions:', err);
    }
  }

  async function handleRollback(entryId: string, targetVersion: number) {
    if (!confirm(`Rollback to v${targetVersion}?`)) return;
    try {
      await rollbackPLQEntry(entryId, targetVersion);
      toast.success(`Rolled back to v${targetVersion}.`);
      await loadEntries();
      await showVersions(entryId);
    } catch (err) {
      console.error('Failed to rollback PLQ entry:', err);
      toast.error('Failed to rollback. Please try again.');
    }
  }

  if (loading) return <div className={styles.loading}>Loading PLQ entries...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 18 }}>PLQ Entries</h3>
        <button className={styles.createBtn} onClick={() => setShowCreate(!showCreate)}>
          + New PLQ Entry
        </button>
      </div>

      {showCreate && (
        <form className={styles.form} onSubmit={handleCreate}>
          <div className={styles.formRow}>
            <input
              className={styles.input}
              placeholder="Entry name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <input
              className={styles.input}
              placeholder="Source"
              value={formData.source}
              onChange={(e) => setFormData({ ...formData, source: e.target.value })}
              required
            />
          </div>
          <div className={styles.formActions}>
            <button type="button" className={styles.actionBtn} onClick={() => setShowCreate(false)}>Cancel</button>
            <button type="submit" className={styles.createBtn}>Create</button>
          </div>
        </form>
      )}

      {versionsFor && (
        <div className={styles.versionsPanel}>
          <h4 style={{ fontFamily: 'var(--font-heading)', fontSize: 14, marginBottom: 8 }}>
            Version History
          </h4>
          {versions.map((v) => (
            <div key={v.id} className={styles.versionItem}>
              <span>
                v{v.version_number}
                {v.is_current && <span style={{ color: 'var(--color-primary)', marginLeft: 8 }}>(current)</span>}
              </span>
              <span style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>
                {v.change_summary} — {new Date(v.created_at).toLocaleString('fr-CH')}
              </span>
              {!v.is_current && (
                <button
                  className={styles.actionBtn}
                  onClick={() => handleRollback(versionsFor, v.version_number)}
                >
                  Rollback
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {entries.length === 0 ? (
        <div className={styles.empty}>No PLQ entries yet.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--color-text-muted)' }}>Name</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--color-text-muted)' }}>Source</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--color-text-muted)' }}>Logements</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--color-text-muted)' }}>SBP Total</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--color-text-muted)' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <td style={{ padding: '8px 12px' }}>{e.name}</td>
                <td style={{ padding: '8px 12px', color: 'var(--color-text-muted)' }}>{e.source}</td>
                <td style={{ padding: '8px 12px' }}>{e.logements ?? '—'}</td>
                <td style={{ padding: '8px 12px' }}>{e.sbp_total ?? '—'}</td>
                <td style={{ padding: '8px 12px' }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      className={`${styles.actionBtn} ${styles.versionBtn}`}
                      onClick={() => showVersions(e.id)}
                    >
                      Versions
                    </button>
                    {e.is_custom && (
                      <button
                        className={`${styles.actionBtn} ${styles.dangerBtn}`}
                        onClick={() => handleDelete(e.id)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
